import { createAsyncThunk } from "@reduxjs/toolkit";
import { Axios } from "api/axios";
import { InventoryAxios } from "api/InventoryAxios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { FilterBuilder } from "helpers/ThunkHelper/FilterBuilder";

export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

export const fetchrecordLevelInstock = createAsyncThunk(
  "inventory/fetchrecordLevelInstock",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    try {
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("page_size", pageSize.toString());
      if (search) params.append("search", search);
      if (sort) {
        const orderingValue = order === "desc" ? `-${sort}` : sort;
        params.append("ordering", orderingValue);
      }

      const filterPayload = FilterBuilder(filters ?? []);
      const body = {
        filters: filterPayload,
      };

      const response: any = await InventoryAxios.post(
        `${API_ENDPOINT.INVENTORY.REORDER_LEVEL.LIST}?${params.toString()}`,
        body
      );
      return {
        data: response.data.data || [],
        rowCount: response.data.count || 0,
      };
    } catch (error: any) {
      console.error(
        "fetch Inventory List error:",
        error.response?.data || error.message
      );
      throw new Error("Network Error");
    }
  }
);

export const addReorderLevel = createAsyncThunk(
  "inventory/addReorderLevel",
  async (values: any, { rejectWithValue }) => {
    try {
      const response = await InventoryAxios.post(
        API_ENDPOINT.INVENTORY.REORDER_LEVEL.CREATE,
        values
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "add Reorder Level error:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to add Reorder Level"
      );
    }
  }
);

export const updateReorderLevel = createAsyncThunk(
  "inventory/updateReorderLevel",
  async ({ id, values }: { id: string; values: any }, { rejectWithValue }) => {
    try {
      const response = await InventoryAxios.patch(
        API_ENDPOINT.INVENTORY.REORDER_LEVEL.UPDATE(id),
        values
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "update Reorder Level error:",
        error.response?.data || error.message
      );
      return rejectWithValue(
        error.response?.data || "Failed to update Reorder Level"
      );
    }
  }
);

export const deleteReorderLevel = createAsyncThunk(
  "inventory/deleteReorderLevel",
  async (id: string) => {
    try {
      const response = await InventoryAxios.delete(
        API_ENDPOINT.INVENTORY.REORDER_LEVEL.DELETE(id)
      );
      return response.data;
    } catch (error: any) {
      console.error(
        "delete Reorder Level error:",
        error.response?.data || error.message
      );
      throw new Error("Failed to delete Reorder Level");
    }
  }
);

export const fetchReorderLevelDropDown = createAsyncThunk(
  "inventory/fetchReorderLevelDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await InventoryAxios.get(
        API_ENDPOINT.INVENTORY.REORDER_LEVEL.DROPDOWN
      );
      return response.data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to fetch reorder level dropdown data"
      );
    }
  }
);

export const fetchReorderLevelDetail = createAsyncThunk(
  "inventory/fetchReorderLevelDetail",
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await InventoryAxios.get(
        API_ENDPOINT.INVENTORY.REORDER_LEVEL.DETAIL(id)
      );
      return response.data;
    } catch (error: any) {
      console.error("fetch Reorder Level detail error:", error.response?.data || error.message);
      return rejectWithValue(error.response?.data || "Failed to fetch Reorder Level details");
    }
  }
);

export const exportReorderLevelCSV = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any[];
}) => {
  const params = new URLSearchParams();

  if (search) params.append("search", search);

  const filterPayload = FilterBuilder(filters ?? []);

  const body = {
    filters: filterPayload,
  };

  const response = await InventoryAxios.post(
    `${API_ENDPOINT.INVENTORY.REORDER_LEVEL.EXPORT}?${params.toString()}`,
    body,
    { responseType: "blob" }
  );

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") ||
      `reorder_level_${new Date().toISOString().split("T")[0]}.csv`,
  };
};

